// ============================================================================
// [임시] 자문(Consult) API
// - 실제 CRUD 백엔드가 준비되면 이 파일의 함수 "구현부"만 교체하면 된다(시그니처 유지).
// - 지금은 localStorage 에 저장해 UI 가 실제로 동작하도록 한다.
// ============================================================================

export interface Consult {
  consultId: number;
  postId: number;
  author: string;
  content: string; // free text
  createdAt: string; // ISO
  updatedAt?: string; // ISO
}

const STORAGE_PREFIX = "atlas-consult-temp-";
const storageKey = (postId: number) => `${STORAGE_PREFIX}${postId}`;

const readAll = (postId: number): Consult[] => {
  try {
    const raw = window.localStorage.getItem(storageKey(postId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeAll = (postId: number, list: Consult[]) => {
  try {
    window.localStorage.setItem(storageKey(postId), JSON.stringify(list));
  } catch {
    /* noop */
  }
};

// 실제 네트워크처럼 보이도록 약간의 지연
const delay = (ms = 150) => new Promise((resolve) => setTimeout(resolve, ms));

// 최신순(위에서부터 누적) 정렬해서 반환
export const getConsults = async (postId: number): Promise<Consult[]> => {
  await delay();
  return readAll(postId).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
};

export const createConsult = async (
  postId: number,
  content: string,
  author: string
): Promise<Consult> => {
  await delay();
  const now = new Date().toISOString();
  const consult: Consult = {
    consultId: Date.now(),
    postId,
    author,
    content,
    createdAt: now,
  };
  writeAll(postId, [consult, ...readAll(postId)]);
  return consult;
};

export const updateConsult = async (
  postId: number,
  consultId: number,
  content: string
): Promise<Consult | null> => {
  await delay();
  const list = readAll(postId);
  const idx = list.findIndex((c) => c.consultId === consultId);
  if (idx === -1) return null;
  const updated: Consult = { ...list[idx], content, updatedAt: new Date().toISOString() };
  list[idx] = updated;
  writeAll(postId, list);
  return updated;
};

export const deleteConsult = async (postId: number, consultId: number): Promise<void> => {
  await delay();
  writeAll(postId, readAll(postId).filter((c) => c.consultId !== consultId));
};

// ============================================================================
// [임시] Post 정보(성별/나이/장기/임상정보) & 자문글 여부
// - 추후 Post 데이터에 필드가 추가되면, 아래 extractor 가 그대로 그 필드를 읽는다.
// - 현재는 postData 에 있으면 읽고 없으면 undefined(placeholder 처리).
// ============================================================================

export interface ConsultPostInfo {
  gender?: string;
  age?: string;
  organ?: string;
  clinicalInfo?: string;
}

export const extractConsultPostInfo = (postData: any): ConsultPostInfo => ({
  gender: postData?.gender ?? postData?.sex ?? undefined,
  age:
    postData?.age !== undefined && postData?.age !== null && postData?.age !== ""
      ? String(postData.age)
      : undefined,
  organ: postData?.organ ?? postData?.organName ?? undefined,
  clinicalInfo: postData?.clinicalInfo ?? postData?.clinicalInformation ?? undefined,
});

// 이 게시글이 자문 게시글인지 여부(임시). 추후 Post 데이터의 플래그로 대체.
export const isConsultPost = (postData: any): boolean => {
  if (postData == null) return false;
  if (typeof postData.isConsult === "boolean") return postData.isConsult;
  if (typeof postData.isConsultation === "boolean") return postData.isConsultation;
  return true; // 임시: 플래그가 아직 없으면 자문글로 간주
};
